'use client'

import {
    createMedicalSupply,
    getMedicalSupply,
    HEALTH_ONLY_SUPPLY_TYPES,
    MEDICAL_SUPPLY_TYPE_LABELS,
    MedicalSupplyType,
    NEGOTIATION_MODALITY_LABELS,
    NegotiationModality,
    PRODUCT_ORIGIN_LABELS,
    PRODUCT_UNIT_LABELS,
    ProductOrigin,
    ProductUnit,
    updateMedicalSupply,
} from '@/lib/api/medical-supplies/medical-supplies-api'
import { mask, unMask } from '@/lib/form-state/util'
import { SupplierSelector } from '@/components/SupplierSelector'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import { MdArrowBack, MdSave } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

// ==================== Utilitários de moeda ====================

const currencyFormat = (
    value: string | number | null | undefined,
    allowEmpty = false
) => {
    if (value === null || value === undefined) value = '0'
    const digits = `${value}`.replace(/[^0-9]+/g, '')
    if (
        allowEmpty &&
        (!digits || digits === '0' || digits === '00' || digits === '000')
    )
        return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue)
}

const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined || cents === 0) return ''
    return currencyFormat(cents.toString())
}

// ==================== Tipos ====================

const SUPPLY_TYPES: MedicalSupplyType[] = [
    'OPME',
    'PRODUCT',
    'DISPOSABLE_KIT',
    'INFECTION_PREVENTION_BUNDLE',
    'PACKAGE',
    'SURGICAL_MATERIAL',
    'INSTRUMENT',
    'IMPLANT',
    'EQUIPMENT',
]

const UNITS: ProductUnit[] = [
    'UNIT',
    'KG',
    'GRAM',
    'LITER',
    'ML',
    'METER',
    'CM',
    'HOUR',
    'SESSION',
    'PACK',
    'BOX',
    'PAIR',
    'DOZEN',
]

const ORIGINS: ProductOrigin[] = [
    'NATIONAL',
    'IMPORTED',
    'IMPORTED_NATIONAL_CONTENT',
    'NATIONAL_IMPORTED_CONTENT',
]

const MODALITIES: NegotiationModality[] = ['SALE', 'RENTAL']

// ==================== Componente ====================

export default function MedicalSuppliesFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    // Campos do formulário
    const [name, setName] = useState('')
    const [type, setType] = useState<MedicalSupplyType>('PRODUCT')
    const [description, setDescription] = useState('')
    // Continua adiante do state de `type` porque o tipo JÁ gravado nunca sai da
    // lista: escondê-lo na edição deixaria o select apontando para um valor
    // inexistente e trocaria o tipo do material no primeiro save.
    const isHealth = user?.company?.segment === 'HEALTH'
    const visibleSupplyTypes = useMemo(
        () =>
            isHealth
                ? SUPPLY_TYPES
                : SUPPLY_TYPES.filter(
                      t => t === type || !HEALTH_ONLY_SUPPLY_TYPES.includes(t)
                  ),
        [isHealth, type]
    )
    const [code, setCode] = useState('')
    const [manufacturer, setManufacturer] = useState('')
    const [brand, setBrand] = useState('')
    const [anvisaCode, setAnvisaCode] = useState('')
    const [unit, setUnit] = useState<ProductUnit>('UNIT')
    const [quantity, setQuantity] = useState('1')
    const [isHospital, setIsHospital] = useState(false)
    const [origin, setOrigin] = useState<ProductOrigin>('NATIONAL')
    const [negotiationModality, setNegotiationModality] =
        useState<NegotiationModality>('SALE')
    const [costPrice, setCostPrice] = useState('')
    const [sellingPrice, setSellingPrice] = useState('')
    const [markupPercentage, setMarkupPercentage] = useState('')
    const [maxDiscount, setMaxDiscount] = useState('')
    const [supplierId, setSupplierId] = useState<number | null>(null)
    const [active, setActive] = useState(true)

    useEffect(() => {
        if (isEditing && id) {
            setLoading(true)
            getMedicalSupply(parseInt(id))
                .then(res => {
                    if (res.success) {
                        const item = res.result
                        setName(item.name)
                        setType(item.type)
                        setDescription(item.description || '')
                        setCode(item.code || '')
                        setManufacturer(item.manufacturer || '')
                        setBrand(item.brand || '')
                        setAnvisaCode(item.anvisaCode || '')
                        setUnit(item.unit)
                        setQuantity(item.quantity.toString())
                        setIsHospital(item.isHospital)
                        setOrigin(item.origin)
                        setNegotiationModality(item.negotiationModality)
                        setCostPrice(formatCentsToDisplay(item.costPrice))
                        setSellingPrice(formatCentsToDisplay(item.sellingPrice))
                        setMarkupPercentage(
                            item.markupPercentage != null
                                ? item.markupPercentage.toString()
                                : ''
                        )
                        setMaxDiscount(formatCentsToDisplay(item.maxDiscount))
                        setSupplierId(item.supplierId || null)
                        setActive(item.active)
                    } else {
                        toast.error('Erro ao carregar material')
                        navigate('/registrations/supplies')
                    }
                })
                .finally(() => setLoading(false))
        }
    }, [id, isEditing, navigate])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }
        if (!type) {
            toast.error('Tipo é obrigatório')
            return
        }

        setSaving(true)
        try {
            const data: any = {
                name: name.trim(),
                type,
                description: description.trim() || undefined,
                code: code.trim() || undefined,
                manufacturer: manufacturer.trim() || undefined,
                brand: brand.trim() || undefined,
                anvisaCode: anvisaCode.trim() || undefined,
                unit,
                quantity: parseInt(quantity) || 1,
                isHospital,
                origin,
                negotiationModality,
                costPrice: parseCurrencyToCents(costPrice),
                sellingPrice: sellingPrice
                    ? parseCurrencyToCents(sellingPrice)
                    : undefined,
                markupPercentage: markupPercentage
                    ? parseFloat(markupPercentage)
                    : undefined,
                maxDiscount: maxDiscount
                    ? parseCurrencyToCents(maxDiscount)
                    : undefined,
                supplierId: supplierId || null,
                active,
                organizationId: user!.organizationId!,
            }

            const response = isEditing
                ? await updateMedicalSupply(parseInt(id!), data)
                : await createMedicalSupply(data)

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Material atualizado com sucesso'
                        : 'Material criado com sucesso'
                )
                navigate('/registrations/supplies')
            } else {
                toast.error(response.error || 'Erro ao salvar material')
            }
        } catch {
            toast.error('Erro ao salvar material')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/registrations/supplies')}
                    className="btn btn-ghost btn-sm btn-circle"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                    {isEditing
                        ? 'Editar Material'
                        : 'Novo Material'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Identificação */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Identificação
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome *
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Ex: Arco Cirúrgico"
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo *
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={type}
                                onChange={e =>
                                    setType(e.target.value as MedicalSupplyType)
                                }
                                required
                            >
                                {visibleSupplyTypes.map(t => (
                                    <option key={t} value={t}>
                                        {MEDICAL_SUPPLY_TYPE_LABELS[t]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Código/Referência
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={code}
                                onChange={e => setCode(e.target.value)}
                                placeholder="Código interno"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Registro ANVISA
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={anvisaCode}
                                onChange={e => setAnvisaCode(e.target.value)}
                                placeholder="Número de registro"
                            />
                        </div>

                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Descrição
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Descrição do material"
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                {/* Detalhes */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Detalhes
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Fabricante
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={manufacturer}
                                onChange={e => setManufacturer(e.target.value)}
                                placeholder="Nome do fabricante"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Marca
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={brand}
                                onChange={e => setBrand(e.target.value)}
                                placeholder="Marca"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Unidade de Medida
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={unit}
                                onChange={e =>
                                    setUnit(e.target.value as ProductUnit)
                                }
                            >
                                {UNITS.map(u => (
                                    <option key={u} value={u}>
                                        {PRODUCT_UNIT_LABELS[u]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Quantidade
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                value={quantity}
                                onChange={e => setQuantity(e.target.value)}
                                min="1"
                                placeholder="1"
                            />
                        </div>
                    </div>
                </div>

                {/* Classificação */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Classificação
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Origem
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={origin}
                                onChange={e =>
                                    setOrigin(e.target.value as ProductOrigin)
                                }
                            >
                                {ORIGINS.map(o => (
                                    <option key={o} value={o}>
                                        {PRODUCT_ORIGIN_LABELS[o]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Modalidade de Negociação
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={negotiationModality}
                                onChange={e =>
                                    setNegotiationModality(
                                        e.target.value as NegotiationModality
                                    )
                                }
                            >
                                {MODALITIES.map(m => (
                                    <option key={m} value={m}>
                                        {NEGOTIATION_MODALITY_LABELS[m]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Marca de material hospitalar: só na saúde. O valor já
                            gravado é preservado — o checkbox some da tela, não do
                            registro. */}
                        {isHealth && (
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={isHospital}
                                        onChange={e =>
                                            setIsHospital(e.target.checked)
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Material Hospitalar
                                    </span>
                                </label>
                            </div>
                        )}

                        {isEditing && (
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={active}
                                        onChange={e =>
                                            setActive(e.target.checked)
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Ativo
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                {/* Preços */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Preços
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Preço de Custo *
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={costPrice}
                                onChange={e =>
                                    setCostPrice(
                                        currencyFormat(e.target.value, true)
                                    )
                                }
                                placeholder="R$ 0,00"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Preço de Venda
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={sellingPrice}
                                onChange={e => {
                                    const formatted = currencyFormat(
                                        e.target.value,
                                        true
                                    )
                                    setSellingPrice(formatted)
                                    const costCents =
                                        parseCurrencyToCents(costPrice)
                                    const sellCents =
                                        parseCurrencyToCents(formatted)
                                    if (costCents > 0 && sellCents > 0) {
                                        const markup =
                                            ((sellCents - costCents) /
                                                costCents) *
                                            100
                                        setMarkupPercentage(markup.toFixed(2))
                                    } else {
                                        setMarkupPercentage('')
                                    }
                                }}
                                placeholder="R$ 0,00"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Markup (%)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                value={markupPercentage}
                                onChange={e => {
                                    const value = e.target.value
                                    setMarkupPercentage(value)
                                    const costCents =
                                        parseCurrencyToCents(costPrice)
                                    if (costCents > 0 && value !== '') {
                                        const markup = parseFloat(value)
                                        if (!isNaN(markup)) {
                                            const sellCents = Math.round(
                                                costCents * (1 + markup / 100)
                                            )
                                            setSellingPrice(
                                                formatCentsToDisplay(sellCents)
                                            )
                                        }
                                    } else if (value === '') {
                                        setSellingPrice('')
                                    }
                                }}
                                step="0.01"
                                min="0"
                                placeholder="0,00"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Desconto Máximo
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                value={maxDiscount}
                                onChange={e =>
                                    setMaxDiscount(
                                        currencyFormat(e.target.value, true)
                                    )
                                }
                                placeholder="R$ 0,00"
                            />
                        </div>
                    </div>
                </div>

                {/* Fornecedor */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Fornecedor
                    </h2>
                    {/* Selector com busca, o mesmo de produtos e contas a pagar. O
                        `select` simples obrigava a rolar a lista inteira de
                        fornecedores da organização para achar um. */}
                    <div className="form-control">
                        <SupplierSelector
                            value={supplierId}
                            onChange={id => setSupplierId(id)}
                            label="Fornecedor (opcional)"
                            placeholder="Buscar fornecedor..."
                        />
                    </div>
                </div>

                {/* Botões */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() =>
                            navigate('/registrations/supplies')
                        }
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar Alterações' : 'Criar Material'}
                    </button>
                </div>
            </form>
        </div>
    )
}
